import { RequirePayoutsAccountingSettingsUser } from '@/components/auth/RequirePayoutsAccountingSettingsUser';
import {
  useGetOrganizationHoldbackPaymentBatchedSettingsQuery,
  useGetOrganizationHoldbackPayoutEventSettingsQuery,
  useGetOrganizationPausedSettingsQuery,
  usePutOrganizationHoldbackPaymentBatchedSettingsMutation,
  usePutOrganizationHoldbackPayoutEventSettingsMutation,
  usePutOrganizationPausedSettingsMutation,
  PutOrganizationPausedSettings,
} from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { Box, Table, useOpenClose } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  HoldbackPaymentBatchedSettings,
  HoldbackPaymentBatchedSettingsCadence,
  HoldbackPaymentBatchedSettingsFrequencyMap,
  HoldbackPayoutEvent,
  HoldbackPayoutEvents,
  HoldbackPayoutEventsSettings,
  OrganizationSettingsTypes,
  PayoutEvent,
  PayoutEventMap,
  ProgramType,
} from 'types';

import { getHoldbackScheduleLabel } from '../../../../helpers/holdbackScheduleLabel';
import { OrganizationSettingsCard } from '../shared/OrganizationSettingsCard';
import { PausedSettingsFormValues, PausedSettingsEditModal } from '../shared/PausedSettingsEditModal';

import { HoldbackPaymentBatchedSettingsEditModal } from './HoldbackPaymentBatchedSettingsEditModal';
import { HoldbackPayoutEventsSettingsEditModal } from './HoldbackPayoutEventsSettingsEditModal';

const columnHoldbackPayoutEventSettings = [
  {
    heading: 'Rule Name',
    dataKey: 'name',
    width: 200,
  },
  {
    heading: 'Setting',
    dataKey: 'setting',
    width: 100,
    align: 'center' as const,
  },
];

const columnHoldbackPaymentBatchedSettings = [
  {
    heading: 'Frequency',
    dataKey: 'frequency',
    width: 200,
  },
  {
    heading: 'Cadence',
    dataKey: 'cadence',
    width: 100,
    align: 'center' as const,
  },
];

const columnPausedSettings = [
  {
    heading: 'Name',
    dataKey: 'name',
    width: 200,
  },
  {
    heading: 'Value',
    dataKey: 'value',
    width: 100,
    align: 'center' as const,
  },
];

const renderEditButton = (button: ReactNode) => (
  <RequirePayoutsAccountingSettingsUser>{button}</RequirePayoutsAccountingSettingsUser>
);

interface HoldbackSettingsProps {
  organization: OrganizationNode;
  programType: ProgramType;
}

interface HoldbackPayoutEventsFormValues {
  installApproved: 'true' | 'false';
  activationApproved: 'true' | 'false';
  ptoApproved: 'true' | 'false';
  inherit: boolean;
}

export const HoldbackSettings = ({ organization, programType }: HoldbackSettingsProps) => {
  const { alias } = useParams<{ alias: any }>();
  const { enableHoldbackPausedSettings = false } = useFlags();

  const {
    data: holdbackPayoutEventSettings,
    isFetching: isLoadingHoldbackPayoutEventSettings,
    error: holdbackPayoutEventSettingsError,
  } = useGetOrganizationHoldbackPayoutEventSettingsQuery({
    alias,
    programType,
  });

  const [setHoldbackPayoutEventSettings] = usePutOrganizationHoldbackPayoutEventSettingsMutation();
  const {
    handleClose: handleCloseHoldbackPayoutEventSettings,
    handleOpen: handleOpenHoldbackPayoutEventSettings,
    isOpen: isOpenHoldbackPayoutEventSettings,
  } = useOpenClose();

  const {
    data: holdbackPaymentBatchedSettings,
    isFetching: isLoadingHoldbackPaymentBatchedSettings,
    error: holdbackPaymentBatchedSettingsError,
  } = useGetOrganizationHoldbackPaymentBatchedSettingsQuery({
    alias,
    programType,
  });
  const [setHoldbackPaymentBatchedSettings] = usePutOrganizationHoldbackPaymentBatchedSettingsMutation();
  const {
    handleClose: handleCloseHoldbackPaymentBatchedSettings,
    handleOpen: handleOpenHoldbackPaymentBatchedSettings,
    isOpen: isOpenHoldbackPaymentBatchedSettings,
  } = useOpenClose();

  const {
    data: standardHoldbackPausedSettings,
    isFetching: isLoadingStandardHoldbackPausedSettings,
    error: standardHoldbackPausedSettingsError,
  } = useGetOrganizationPausedSettingsQuery(
    {
      alias,
      programType,
      settingsType: OrganizationSettingsTypes.standardHoldbackPausedSettings,
    },
    {
      skip: !enableHoldbackPausedSettings || !alias,
    },
  );
  const [setStandardHoldbackPausedSettings] = usePutOrganizationPausedSettingsMutation();
  const {
    handleClose: handleCloseStandardHoldbackPausedSettings,
    handleOpen: handleOpenStandardHoldbackPausedSettings,
    isOpen: isOpenStandardHoldbackPausedSettings,
  } = useOpenClose();

  const {
    data: domesticContentHoldbackPausedSettings,
    isFetching: isLoadingDomesticContentHoldbackPausedSettings,
    error: domesticContentHoldbackPausedSettingsError,
  } = useGetOrganizationPausedSettingsQuery(
    {
      alias,
      programType,
      settingsType: OrganizationSettingsTypes.domesticContentHoldbackPausedSettings,
    },
    {
      skip: !enableHoldbackPausedSettings || !alias,
    },
  );
  const [setDomesticContentHoldbackPausedSettings] = usePutOrganizationPausedSettingsMutation();
  const {
    handleClose: handleCloseDomesticContentHoldbackPausedSettings,
    handleOpen: handleOpenDomesticContentHoldbackPausedSettings,
    isOpen: isOpenDomesticContentHoldbackPausedSettings,
  } = useOpenClose();

  const handleSubmitHoldbackPayoutEventSettings = useCallback(
    async (values: HoldbackPayoutEventsFormValues) => {
      const holdbackPayoutEvents: HoldbackPayoutEvent[] = HoldbackPayoutEvents.map((event) => ({
        event,
        active: values[event as keyof Omit<HoldbackPayoutEventsFormValues, 'inherit'>] === 'true',
      }));

      const payload = {
        alias,
        programType,
        value: {
          inherit: values.inherit,
          ...(!values.inherit && { holdbackPayoutEvents }),
        } as HoldbackPayoutEventsSettings,
      };

      await setHoldbackPayoutEventSettings(payload).unwrap();
    },
    [alias, programType, setHoldbackPayoutEventSettings],
  );

  const handleSubmitHoldbackPaymentBatchedSettings = useCallback(
    async (values: any) => {
      const payload = {
        alias,
        programType,
        value: {
          inherit: values.inherit,
          ...(!values.inherit && {
            payoutSchedule: {
              cadence: HoldbackPaymentBatchedSettingsCadence.monthly,
              frequency: values.frequency,
              day: values.day,
              occurrences: values.occurrences,
            },
          }),
        } as HoldbackPaymentBatchedSettings,
      };
      await setHoldbackPaymentBatchedSettings(payload).unwrap();
    },
    [alias, programType, setHoldbackPaymentBatchedSettings],
  );

  const handleSubmitStandardHoldbackPausedSettings = useCallback(
    async (values: PausedSettingsFormValues) => {
      const payload = {
        alias,
        programType,
        settingsType: OrganizationSettingsTypes.standardHoldbackPausedSettings,
        value: {
          inherit: values.inherit,
          ...(!values.inherit && { pausedDays: values.pausedDays }),
        },
      } satisfies PutOrganizationPausedSettings;
      await setStandardHoldbackPausedSettings(payload).unwrap();
    },
    [alias, programType, setStandardHoldbackPausedSettings],
  );

  const handleSubmitDomesticContentHoldbackPausedSettings = useCallback(
    async (values: PausedSettingsFormValues) => {
      const payload = {
        alias,
        programType,
        settingsType: OrganizationSettingsTypes.domesticContentHoldbackPausedSettings,
        value: {
          inherit: values.inherit,
          ...(!values.inherit && { pausedDays: values.pausedDays }),
        },
      } satisfies PutOrganizationPausedSettings;
      await setDomesticContentHoldbackPausedSettings(payload).unwrap();
    },
    [alias, programType, setDomesticContentHoldbackPausedSettings],
  );

  const holdbackPayoutEventSettingsNames = useMemo(() => {
    return Object.fromEntries(PayoutEventMap[programType].map((e) => [e.event, e.name]));
  }, [programType]);

  const holdbackPayoutEventSettingsRows = useMemo(() => {
    const events = holdbackPayoutEventSettings?.value?.holdbackPayoutEvents ?? [];
    const filteredEvents = enableHoldbackPausedSettings
      ? events
      : events.filter((item) => item.event !== PayoutEvent.ptoApproved);

    const eventOrderMap = new Map(PayoutEventMap[programType].map((e) => [e.event, e.order]));

    return [...filteredEvents]
      .toSorted((a, b) => (eventOrderMap.get(a.event) ?? 99) - (eventOrderMap.get(b.event) ?? 99))
      .map((item, index) => ({
        id: index,
        name: holdbackPayoutEventSettingsNames[item.event] ?? item.event,
        setting: item.active ? 'Yes' : 'No',
      }));
  }, [holdbackPayoutEventSettings, holdbackPayoutEventSettingsNames, enableHoldbackPausedSettings, programType]);

  const holdbackPaymentBatchedSettingsRows = useMemo(() => {
    const payoutSchedule = holdbackPaymentBatchedSettings?.value?.payoutSchedule;
    if (!payoutSchedule) return [];

    const frequencyLabel = HoldbackPaymentBatchedSettingsFrequencyMap[payoutSchedule.frequency];
    const cadenceLabel = getHoldbackScheduleLabel(
      payoutSchedule.frequency,
      payoutSchedule.day,
      payoutSchedule.occurrences,
    );

    return [
      {
        id: 'payoutSchedule',
        frequency: frequencyLabel,
        cadence: cadenceLabel,
      },
    ];
  }, [holdbackPaymentBatchedSettings]);

  const standardHoldbackPausedSettingsRows = useMemo(() => {
    const pausedDays = standardHoldbackPausedSettings?.value?.pausedDays;
    if (pausedDays === undefined) return [];

    return [
      {
        id: 'pausedDays',
        name: 'Paused Days',
        value: pausedDays,
      },
    ];
  }, [standardHoldbackPausedSettings]);

  const domesticContentHoldbackPausedSettingsRows = useMemo(() => {
    const pausedDays = domesticContentHoldbackPausedSettings?.value?.pausedDays;
    if (pausedDays === undefined) return [];

    return [
      {
        id: 'pausedDays',
        name: 'Paused Days',
        value: pausedDays,
      },
    ];
  }, [domesticContentHoldbackPausedSettings]);

  return (
    <>
      <HoldbackPayoutEventsSettingsEditModal
        isOpen={isOpenHoldbackPayoutEventSettings}
        handleClose={handleCloseHoldbackPayoutEventSettings}
        handleSubmit={handleSubmitHoldbackPayoutEventSettings}
        currentHoldbackSettings={holdbackPayoutEventSettings?.value}
        modalId="edit-holdback-payout-event-settings"
        modalTitle="Edit Holdback Transaction Creation"
        showPtoApproved={enableHoldbackPausedSettings}
      />
      <OrganizationSettingsCard
        cardTitle="Holdback Transaction Created"
        organization={organization}
        isLoading={isLoadingHoldbackPayoutEventSettings}
        error={holdbackPayoutEventSettingsError}
        isInherited={!holdbackPayoutEventSettingsError && holdbackPayoutEventSettings?.value?.inherit}
        onClick={handleOpenHoldbackPayoutEventSettings}
        cardPadding="0 md xl"
        renderEditButton={renderEditButton}
      >
        <Table
          rowKey="id"
          columns={columnHoldbackPayoutEventSettings}
          rows={holdbackPayoutEventSettingsRows}
          isLoading={isLoadingHoldbackPayoutEventSettings}
        />
      </OrganizationSettingsCard>

      {!enableHoldbackPausedSettings && (
        <>
          <HoldbackPaymentBatchedSettingsEditModal
            isOpen={isOpenHoldbackPaymentBatchedSettings}
            handleClose={handleCloseHoldbackPaymentBatchedSettings}
            handleSubmit={handleSubmitHoldbackPaymentBatchedSettings}
            currentSettings={holdbackPaymentBatchedSettings?.value}
            modalId="edit-holdback-payment-batched-settings"
            modalTitle="Edit Holdback Batched for Payment"
          />
          <Box margin="md 0 0 0">
            <OrganizationSettingsCard
              cardTitle="Holdback Batched for Payment"
              organization={organization}
              isLoading={isLoadingHoldbackPaymentBatchedSettings}
              error={holdbackPaymentBatchedSettingsError}
              isInherited={!holdbackPaymentBatchedSettingsError && holdbackPaymentBatchedSettings?.value?.inherit}
              onClick={handleOpenHoldbackPaymentBatchedSettings}
              cardPadding="0 md xl"
              renderEditButton={renderEditButton}
            >
              <Table
                rowKey="id"
                columns={columnHoldbackPaymentBatchedSettings}
                rows={holdbackPaymentBatchedSettingsRows}
                isLoading={isLoadingHoldbackPaymentBatchedSettings}
              />
            </OrganizationSettingsCard>
          </Box>
        </>
      )}
      {enableHoldbackPausedSettings && (
        <>
          <PausedSettingsEditModal
            isOpen={isOpenStandardHoldbackPausedSettings}
            handleClose={handleCloseStandardHoldbackPausedSettings}
            handleSubmit={handleSubmitStandardHoldbackPausedSettings}
            currentSettings={standardHoldbackPausedSettings?.value}
            modalId="edit-standard-holdback-paused-settings"
            modalTitle="Edit Standard Holdback Paused Days"
          />
          <Box margin="md 0 0 0">
            <OrganizationSettingsCard
              cardTitle="Standard Holdback Paused Days"
              organization={organization}
              isLoading={isLoadingStandardHoldbackPausedSettings}
              error={standardHoldbackPausedSettingsError}
              isInherited={!standardHoldbackPausedSettingsError && standardHoldbackPausedSettings?.value?.inherit}
              onClick={handleOpenStandardHoldbackPausedSettings}
              cardPadding="0 md xl"
              renderEditButton={renderEditButton}
            >
              <Table
                rowKey="id"
                columns={columnPausedSettings}
                rows={standardHoldbackPausedSettingsRows}
                isLoading={isLoadingStandardHoldbackPausedSettings}
              />
            </OrganizationSettingsCard>
          </Box>

          <PausedSettingsEditModal
            isOpen={isOpenDomesticContentHoldbackPausedSettings}
            handleClose={handleCloseDomesticContentHoldbackPausedSettings}
            handleSubmit={handleSubmitDomesticContentHoldbackPausedSettings}
            currentSettings={domesticContentHoldbackPausedSettings?.value}
            modalId="edit-domestic-content-holdback-paused-settings"
            modalTitle="Edit Domestic Content Holdback Paused Days"
          />
          <Box margin="md 0 0 0">
            <OrganizationSettingsCard
              cardTitle="Domestic Content Holdback Paused Days"
              organization={organization}
              isLoading={isLoadingDomesticContentHoldbackPausedSettings}
              error={domesticContentHoldbackPausedSettingsError}
              isInherited={
                !domesticContentHoldbackPausedSettingsError && domesticContentHoldbackPausedSettings?.value?.inherit
              }
              onClick={handleOpenDomesticContentHoldbackPausedSettings}
              cardPadding="0 md xl"
              renderEditButton={renderEditButton}
            >
              <Table
                rowKey="id"
                columns={columnPausedSettings}
                rows={domesticContentHoldbackPausedSettingsRows}
                isLoading={isLoadingDomesticContentHoldbackPausedSettings}
              />
            </OrganizationSettingsCard>
          </Box>
        </>
      )}
    </>
  );
};
